/* hs-eslint ignored failing-rules */

/* eslint-disable no-prototype-builtins */
'use es6'; // https://github.com/rjsf-team/react-jsonschema-form/blob/master/packages/core/src/utils.js

import memoizeOne from 'react-utils/memoizeOne';
import { isObject, isFixedItems } from './typeUtils';
import { isMultiSelect } from './fieldTypes';
import { findSchemaDefinition, retrieveSchema } from './schemaUtils';
import fill from './fill';
/**
 * When merging defaults and form data, we want to merge in this specific way:
 * - objects are deeply merged
 * - arrays are merged in such a way that:
 *   - when the array is set in form data, only array entries set in form data
 *     are deeply merged; additional entries from the defaults are ignored
 *   - when the array is not set in form data, the default is copied over
 * - scalars are overwritten/set by form data
 */

export function mergeDefaultsWithFormData(defaults, formData) {
  if (Array.isArray(formData)) {
    if (!Array.isArray(defaults)) {
      defaults = [];
    }

    return formData.map((value, idx) => {
      if (defaults[idx]) {
        return mergeDefaultsWithFormData(defaults[idx], value);
      }

      return value;
    });
  } else if (isObject(formData)) {
    const acc = Object.assign({}, defaults); // Prevent mutation of source object.

    return Object.keys(formData).reduce((_acc, key) => {
      _acc[key] = mergeDefaultsWithFormData(defaults ? defaults[key] : {}, formData[key]);
      return _acc;
    }, acc);
  } else {
    return formData;
  }
}
export function mergeObjects(obj1, obj2, concatArrays = false) {
  // Recursively merge deeply nested objects.
  const acc = Object.assign({}, obj1); // Prevent mutation of source object.

  return Object.keys(obj2).reduce((_acc, key) => {
    const left = obj1 ? obj1[key] : {};
    const right = obj2[key];

    if (obj1 && obj1.hasOwnProperty(key) && isObject(right)) {
      _acc[key] = mergeObjects(left, right, concatArrays);
    } else if (concatArrays && Array.isArray(left) && Array.isArray(right)) {
      _acc[key] = left.concat(right);
    } else {
      _acc[key] = right;
    }

    return _acc;
  }, acc);
}

function computeDefaults(schema, parentDefaults, definitions = {}) {
  // Compute the defaults recursively: give highest priority to deepest nodes.
  let defaults = parentDefaults;

  if (isObject(defaults) && isObject(schema.default)) {
    // For object defaults, only override parent defaults that are defined in
    // schema.default.
    defaults = mergeObjects(defaults, schema.default);
  } else if ('default' in schema) {
    // Use schema defaults for this node.
    defaults = schema.default;
  } else if ('$ref' in schema) {
    // Use referenced schema defaults for this node.
    const refSchema = findSchemaDefinition(schema.$ref, definitions);
    return computeDefaults(refSchema, defaults, definitions);
  } else if (isFixedItems(schema)) {
    defaults = schema.items.map(itemSchema => computeDefaults(itemSchema, undefined, definitions));
  } // Not defaults defined for this node, fallback to generic typed ones.


  if (typeof defaults === 'undefined') {
    defaults = schema.default;
  }

  switch (schema.type) {
    // We need to recur for object schema inner default values.
    case 'object':
      return Object.keys(schema.properties || {}).reduce((acc, key) => {
        // Compute the defaults for this node, with the parent defaults we might
        // have from a previous run: defaults[key].
        acc[key] = computeDefaults(schema.properties[key], (defaults || {})[key], definitions);
        return acc;
      }, {});

    case 'array':
      if (schema.minItems) {
        if (!isMultiSelect(schema, definitions)) {
          const defaultsLength = defaults ? defaults.length : 0;

          if (schema.minItems > defaultsLength) {
            const defaultEntries = defaults || []; // populate the array with the defaults

            const fillerSchema = Array.isArray(schema.items) ? schema.additionalItems : schema.items;
            const fillerEntries = fill(new Array(schema.minItems - defaultsLength), computeDefaults(fillerSchema, fillerSchema.defaults, definitions)); // then fill up the rest with either the item default or empty, up to minItems

            return defaultEntries.concat(fillerEntries);
          }
        } else {
          return [];
        }
      }

      return defaults;

    default:
      return defaults;
  }
}

export function getDefaultFormState(_schema, formData, definitions = {}) {
  if (!isObject(_schema)) {
    throw new Error(`Invalid schema: ${_schema}`);
  }

  const schema = retrieveSchema(_schema, definitions, formData);
  const defaults = computeDefaults(schema, _schema.default, definitions);

  if (typeof formData === 'undefined') {
    // No form data? Use schema defaults.
    return defaults;
  }

  if (isObject(formData)) {
    // Override schema defaults with form data.
    return mergeObjects(defaults, formData);
  }

  return formData || defaults;
}
export const memoizedGetDefaultFormState = memoizeOne(getDefaultFormState);
export function getDefaultRegistry() {
  return {
    definitions: {},
    formContext: {}
  };
}